import { CodeItemGroup, CodeRow, ConditionEntry, ConditionGroup, IfNode, RuleNode } from '../types/codeIndex'
import { formatCodeItemReference, normalizeCodeItemReference } from './codeReference'
import { buildPositionableValues, getCodeItemDigits, getCodeRowPositionEnd } from './compositeCodeUtils'

export type RuleValidationErrorType = 'conflict' | 'range' | 'own-position' | 'impossible'
export type RuleValidationWarningType = 'numeric-operator' | 'dead-trigger' | 'dead-alternative'

export type RuleValidationConditionHighlight = {
  reference: string
  condition: string
  value?: string | null
  from?: string | null
  to?: string | null
  values?: Record<string, string> | null
}

export type RuleValidationError = {
  type: RuleValidationErrorType
  message: string
  localConditions?: RuleValidationConditionHighlight[]
}

export type RuleValidationWarning = {
  type: RuleValidationWarningType
  message: string
  localConditions?: RuleValidationConditionHighlight[]
}

export type RuleValidationContext = {
  groups: CodeItemGroup[]
  targetGroupIndex: number
  targetRowIndex: number
  /**
   * Whether to follow the values this rule requires into their own rules (Rules D and E). Default
   * true; a sheet-wide pass that only reports on rules nobody edited can switch it off, since
   * following requirements for every value on a large sheet costs far more than the rest together.
   */
  followRequiredValues?: boolean
}

/**
 * One comparison inside a rule. `values` carries the position map of a positionable condition
 * (zero-based position -> character); plain conditions carry `value` or `from`/`to`.
 */
export type ConditionAtom = {
  reference: string
  condition: string
  value: string
  from: string
  to: string
  values: Record<string, string> | null
}

type PositionValue = {
  position: number
  value: string
}

const NUMERIC_OPERATORS = new Set(['>', '>=', '<', '<=', 'between'])
const EXCLUSION_OPERATORS = new Set(['!=', '<>', 'notequals', 'notcontains', 'notstartswith', 'notendswith'])

/**
 * Several values on an exclusion operator mean "none of them", so they join with AND; every other
 * operator joins its values with OR.
 */
export function isExclusionOperator(operator: string | null | undefined): boolean {
  return EXCLUSION_OPERATORS.has(String(operator || '').trim().toLowerCase())
}

function normalizeRuleValue(value: string | null | undefined): string {
  return String(value || '').trim()
}

function normalizeOptionPattern(value: string | null | undefined): string {
  return normalizeRuleValue(value).toUpperCase()
}

function sameReference(left: string | null | undefined, right: string | null | undefined): boolean {
  return normalizeCodeItemReference(left) === normalizeCodeItemReference(right)
}

function formatOperator(condition: string): string {
  if (condition === '==') return '='
  return condition
}

function getPositionValues(entry: ConditionEntry | null | undefined): PositionValue[] {
  if (!entry?.values) return []

  return Object.entries(entry.values)
    .map(([position, value]) => ({ position: Number(position), value: normalizeOptionPattern(value) }))
    .filter(item => Number.isInteger(item.position) && item.position >= 0 && item.value.length > 0)
    .sort((left, right) => left.position - right.position)
}

function formatConditionAtom(atom: ConditionAtom): string {
  if (atom.values) {
    const positionValues = getPositionValues({ reference: '', condition: '', values: atom.values })
    const positions = positionValues.map(item => item.position + 1)
    const label = positions.length === 1 ? `position ${positions[0]}` : `positions ${positions.join(', ')}`
    const fragment = positionValues.map(item => item.value).join('')
    return `${formatCodeItemReference(atom.reference)} ${label} ${formatOperator(atom.condition)} ${fragment}`
  }

  if (atom.condition === 'between') {
    return `${formatCodeItemReference(atom.reference)} between ${atom.from} and ${atom.to}`
  }

  return `${formatCodeItemReference(atom.reference)} ${formatOperator(atom.condition)} ${atom.value}`
}

function conditionAtomToHighlight(atom: ConditionAtom): RuleValidationConditionHighlight {
  return {
    reference: atom.reference,
    condition: atom.condition,
    value: atom.value || null,
    from: atom.from || null,
    to: atom.to || null,
    values: atom.values || null,
  }
}

function parseNumericValue(value: string): number | null {
  if (!value) return null

  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : null
}

function conditionEntryToAtom(condition: ConditionEntry | null | undefined): ConditionAtom | null {
  if (!condition) return null

  const reference = normalizeRuleValue(condition.reference)
  const operator = normalizeRuleValue(condition.condition)
  const value = normalizeRuleValue(condition.value)
  const from = normalizeRuleValue(condition.from)
  const to = normalizeRuleValue(condition.to)

  if (!reference || !operator) return null

  const positionValues = getPositionValues(condition)
  if (positionValues.length > 0) {
    return {
      reference,
      condition: operator,
      value: '',
      from: '',
      to: '',
      values: Object.fromEntries(positionValues.map(item => [String(item.position), item.value])),
    }
  }

  if (operator === 'between') {
    if (!from || !to) return null
    return { reference, condition: operator, value: '', from, to, values: null }
  }

  if (!value) return null
  return { reference, condition: operator, value, from: '', to: '', values: null }
}

function combinePathSets(pathSets: ConditionAtom[][][]): ConditionAtom[][] {
  return pathSets.reduce<ConditionAtom[][]>((combinedPaths, currentPaths) => {
    const nextPaths: ConditionAtom[][] = []

    combinedPaths.forEach(path => {
      currentPaths.forEach(currentPath => {
        nextPaths.push([...path, ...currentPath])
      })
    })

    return nextPaths
  }, [[]])
}

function getConditionGroupPaths(group: ConditionGroup | null | undefined): ConditionAtom[][] {
  if (!group || (group.logicalOperator !== 'AND' && group.logicalOperator !== 'OR')) return [[]]

  const conditionPathSets = (group.conditions || [])
    .map(conditionEntryToAtom)
    .filter((atom): atom is ConditionAtom => Boolean(atom))
    .map(atom => [[atom]])

  const nestedGroupPathSets = (group.groups || []).map(getConditionGroupPaths)
  const childPathSets = [...conditionPathSets, ...nestedGroupPathSets]

  if (childPathSets.length === 0) return [[]]

  if (group.logicalOperator === 'OR') {
    return childPathSets.flat()
  }

  return combinePathSets(childPathSets)
}

function getLegacyIfConditionAtom(node: IfNode): ConditionAtom | null {
  return conditionEntryToAtom({
    reference: normalizeRuleValue(node.reference),
    condition: normalizeRuleValue(node.condition),
    value: normalizeRuleValue(node.value),
    from: null,
    to: null,
  })
}

const ruleConditionPathsCache = new WeakMap<object, ConditionAtom[][]>()

function getRuleConditionPaths(rule: RuleNode | null | undefined): ConditionAtom[][] {
  if (!rule || rule.type === 'text') return []

  const cached = ruleConditionPathsCache.get(rule)
  if (cached) return cached

  const paths = buildRuleConditionPaths(rule)
  ruleConditionPathsCache.set(rule, paths)
  return paths
}

function buildRuleConditionPaths(rule: RuleNode): ConditionAtom[][] {
  if (rule.type === 'while') {
    const atom = conditionEntryToAtom({
      reference: rule.reference,
      condition: rule.condition,
      value: rule.value,
      from: null,
      to: null,
    })

    return atom ? [[atom]] : []
  }

  // A text rule carries prose only, so it constrains nothing.
  if (rule.type === 'text') return []

  const pathSets: ConditionAtom[][][] = []
  const legacyAtom = getLegacyIfConditionAtom(rule)

  if (legacyAtom) {
    pathSets.push([[legacyAtom]])
  }

  if (rule.conditionGroup) {
    pathSets.push(getConditionGroupPaths(rule.conditionGroup))
  }

  ;(rule.consequenceGroups || []).forEach(group => {
    pathSets.push(getConditionGroupPaths(group))
  })

  if (pathSets.length === 0) return []
  return combinePathSets(pathSets)
}

/** Trigger side of a rule: the conditions that decide whether the rule applies at all. */
function getRuleTriggerPaths(rule: RuleNode | null | undefined): ConditionAtom[][] {
  if (!rule || rule.type === 'text' || rule.type === 'while') return []

  const pathSets: ConditionAtom[][][] = []
  const legacyAtom = getLegacyIfConditionAtom(rule)

  if (legacyAtom) pathSets.push([[legacyAtom]])
  if (rule.conditionGroup) pathSets.push(getConditionGroupPaths(rule.conditionGroup))

  if (pathSets.length === 0) return []
  return combinePathSets(pathSets)
}

function findPathConflict(path: ConditionAtom[]): { left: ConditionAtom; right: ConditionAtom } | null {
  const equalitiesByReference = new Map<string, ConditionAtom>()
  const inequalitiesByReference = new Map<string, Map<string, ConditionAtom>>()

  for (const atom of path) {
    if (atom.values) continue
    if (atom.condition !== '==' && atom.condition !== '!=') continue

    const referenceKey = normalizeCodeItemReference(atom.reference)

    if (atom.condition === '==') {
      const existingEquality = equalitiesByReference.get(referenceKey)
      if (existingEquality && existingEquality.value !== atom.value) {
        return { left: existingEquality, right: atom }
      }

      const matchingInequality = inequalitiesByReference.get(referenceKey)?.get(atom.value)
      if (matchingInequality) {
        return { left: matchingInequality, right: atom }
      }

      equalitiesByReference.set(referenceKey, atom)
      continue
    }

    const matchingEquality = equalitiesByReference.get(referenceKey)
    if (matchingEquality?.value === atom.value) {
      return { left: matchingEquality, right: atom }
    }

    const inequalitiesForReference = inequalitiesByReference.get(referenceKey) || new Map<string, ConditionAtom>()
    inequalitiesForReference.set(atom.value, atom)
    inequalitiesByReference.set(referenceKey, inequalitiesForReference)
  }

  return null
}

function findContradiction(rule: RuleNode | null | undefined): RuleValidationError | null {
  const paths = getRuleConditionPaths(rule)
  if (paths.length === 0) return null

  const pathConflicts = paths.map(findPathConflict)
  const firstConflict = pathConflicts.find((conflict): conflict is { left: ConditionAtom; right: ConditionAtom } => Boolean(conflict))

  if (!firstConflict || pathConflicts.some(conflict => !conflict)) return null

  return {
    type: 'conflict',
    message: `Cannot save rule. Conflicting conditions found: ${formatConditionAtom(firstConflict.left)} conflicts with ${formatConditionAtom(firstConflict.right)}.`,
    localConditions: [
      conditionAtomToHighlight(firstConflict.left),
      conditionAtomToHighlight(firstConflict.right),
    ],
  }
}

function visitConditionGroupEntries(
  group: ConditionGroup | null | undefined,
  callback: (condition: ConditionEntry) => void
) {
  if (!group) return

  ;(group.conditions || []).forEach(condition => callback(condition))
  ;(group.groups || []).forEach(subgroup => visitConditionGroupEntries(subgroup, callback))
}

function visitIfNodeEntries(
  node: IfNode | null | undefined,
  callback: (condition: ConditionEntry) => void
) {
  if (!node) return

  callback({
    reference: normalizeRuleValue(node.reference),
    condition: normalizeRuleValue(node.condition),
    value: normalizeRuleValue(node.value),
    from: null,
    to: null,
  })

  visitConditionGroupEntries(node.conditionGroup, callback)
  ;(node.consequenceGroups || []).forEach(group => visitConditionGroupEntries(group, callback))
  visitIfNodeEntries(node.if, callback)
  visitIfNodeEntries(node.else, callback)
}

function visitRuleEntries(
  rule: RuleNode | null | undefined,
  callback: (condition: ConditionEntry) => void
) {
  if (!rule || rule.type === 'text') return

  if (rule.type === 'while') {
    callback({
      reference: rule.reference,
      condition: rule.condition,
      value: rule.value,
      from: null,
      to: null,
    })
    return
  }

  visitIfNodeEntries(rule, callback)
}

/**
 * THEN side of a rule: the consequence groups when the rule has them, otherwise the condition
 * group (a plain availability rule's conditions are its constraint).
 */
function visitRuleConstraintEntries(
  rule: RuleNode | null | undefined,
  callback: (condition: ConditionEntry) => void
) {
  if (!rule || rule.type === 'text') return

  if (rule.type === 'while') {
    callback({
      reference: rule.reference,
      condition: rule.condition,
      value: rule.value,
      from: null,
      to: null,
    })
    return
  }

  const consequenceGroups = (rule.consequenceGroups || []).filter(Boolean)
  if (consequenceGroups.length > 0) {
    consequenceGroups.forEach(group => visitConditionGroupEntries(group, callback))
    return
  }

  visitConditionGroupEntries(rule.conditionGroup, callback)
}

// -----------------------------
// Positions
// -----------------------------

/**
 * The positions of a Code Item a condition constrains. A `values` map names them directly; a plain
 * pattern contributes the positions of its non-`*` characters (so a value with no `*` touches every
 * position).
 */
export function getConditionTouchedPositions(
  entry: ConditionEntry | null | undefined,
  digits: number
): number[] {
  if (!entry) return []

  const normalizedDigits = Math.max(1, Math.floor(Number(digits)) || 1)
  const positionValues = getPositionValues(entry)
  if (positionValues.length > 0) {
    return positionValues
      .map(item => item.position)
      .filter(position => position < normalizedDigits)
  }

  const positions = new Set<number>()
  ;[entry.value, entry.from, entry.to].forEach((pattern) => {
    const normalizedPattern = normalizeOptionPattern(pattern)
    if (!normalizedPattern) return
    Object.keys(buildPositionableValues(normalizedPattern, 0, normalizedDigits - 1))
      .forEach(position => positions.add(Number(position)))
  })

  return Array.from(positions).sort((left, right) => left - right)
}

/**
 * The positions of its Code Item a value occupies: `valuePosition..valuePositionEnd`, or every
 * position when the value is not positioned.
 */
export function getValueOwnedPositions(
  row: Pick<CodeRow, 'valuePosition' | 'valuePositionEnd'> | null | undefined,
  digits: number
): number[] {
  const normalizedDigits = Math.max(1, Math.floor(Number(digits)) || 1)
  const allPositions = Array.from({ length: normalizedDigits }, (_unused, position) => position)
  if (!row || !Number.isInteger(row.valuePosition)) return allPositions

  const start = Math.max(0, Number(row.valuePosition))
  const end = Math.min(normalizedDigits - 1, Math.max(start, getCodeRowPositionEnd(row) ?? start))
  return allPositions.filter(position => position >= start && position <= end)
}

function getConditionCharacterAt(
  entry: ConditionEntry | null | undefined,
  position: number
): string {
  const positionValues = getPositionValues(entry)
  if (positionValues.length > 0) {
    return positionValues.find(item => item.position === position)?.value || ''
  }

  const character = normalizeOptionPattern(entry?.value)[position] || ''
  return character === '*' ? '' : character
}

// -----------------------------
// Option matching
// -----------------------------

/**
 * Does an option pattern satisfy one atom? `null` means undecidable (wildcards, mismatched widths,
 * nonnumeric operands) and every caller treats undecidable as "satisfied" so validation never blocks
 * on a comparison it cannot prove false.
 */
function decideOptionMatchesPattern(option: string, pattern: string): boolean | null {
  if (!option || !pattern) return null
  if (option.length !== pattern.length) return null

  for (let position = 0; position < pattern.length; position++) {
    const optionCharacter = option[position]
    const patternCharacter = pattern[position]
    if (optionCharacter === '*' || patternCharacter === '*') continue
    if (optionCharacter !== patternCharacter) return false
  }

  return true
}

function decideOptionMatchesPositionValues(option: string, values: Record<string, string>): boolean | null {
  const positionValues = getPositionValues({ reference: '', condition: '', values })
  if (positionValues.length === 0) return null

  for (const item of positionValues) {
    const optionCharacter = option[item.position]
    if (!optionCharacter) return null
    if (optionCharacter === '*' || item.value === '*') continue
    if (optionCharacter !== item.value) return false
  }

  return true
}

function optionSatisfiesAtom(optionCode: string, atom: ConditionAtom): boolean {
  const option = normalizeOptionPattern(optionCode)
  if (!option) return true

  if (atom.values) {
    const matches = decideOptionMatchesPositionValues(option, atom.values)
    if (matches === null) return true
    return atom.condition === '!=' ? !matches : matches
  }

  if (atom.condition === '==' || atom.condition === '!=') {
    const matches = decideOptionMatchesPattern(option, normalizeOptionPattern(atom.value))
    if (matches === null) return true
    return atom.condition === '!=' ? !matches : matches
  }

  if (atom.condition === 'startsWith' || atom.condition === 'endsWith' || atom.condition === 'contains') {
    const needle = normalizeOptionPattern(atom.value)
    if (!needle || option.includes('*')) return true
    if (atom.condition === 'startsWith') return option.startsWith(needle)
    if (atom.condition === 'endsWith') return option.endsWith(needle)
    return option.includes(needle)
  }

  if (!NUMERIC_OPERATORS.has(atom.condition)) return true

  const optionValue = parseNumericValue(option)
  if (optionValue === null) return true

  if (atom.condition === 'between') {
    const fromValue = parseNumericValue(normalizeOptionPattern(atom.from))
    const toValue = parseNumericValue(normalizeOptionPattern(atom.to))
    if (fromValue === null || toValue === null) return true
    return optionValue >= fromValue && optionValue <= toValue
  }

  const comparisonValue = parseNumericValue(normalizeOptionPattern(atom.value))
  if (comparisonValue === null) return true

  if (atom.condition === '>') return optionValue > comparisonValue
  if (atom.condition === '>=') return optionValue >= comparisonValue
  if (atom.condition === '<') return optionValue < comparisonValue
  return optionValue <= comparisonValue
}

const groupOptionsCache = new WeakMap<object, string[]>()

function getGroupOptions(group: CodeItemGroup): string[] {
  const cached = groupOptionsCache.get(group)
  if (cached) return cached

  const options = group.rows
    .map(row => normalizeOptionPattern(row.optionCode))
    .filter(option => option.length > 0)
  groupOptionsCache.set(group, options)
  return options
}

/** The options of a Code Item that satisfy every atom in an AND path for that Code Item. */
export function getMatchingOptions(group: CodeItemGroup | null | undefined, atoms: ConditionAtom[]): string[] {
  if (!group) return []

  return getGroupOptions(group).filter(option => atoms.every(atom => optionSatisfiesAtom(option, atom)))
}

// -----------------------------
// Rule A: own sub-position
// -----------------------------

/**
 * A rule may not constrain the sub-position its own value occupies. On a single-position Code Item
 * that means it cannot reference its own Code Item at all; on a composite one it may still constrain
 * the other sub-positions.
 */
export function findOwnSubPositionError(
  rule: RuleNode | null | undefined,
  context: RuleValidationContext | null | undefined
): RuleValidationError | null {
  if (!rule || rule.type === 'text' || !context) return null

  const group = context.groups[context.targetGroupIndex]
  const row = group?.rows[context.targetRowIndex]
  if (!group || !row) return null

  const optionCode = normalizeOptionPattern(row.optionCode)
  const ownReference = normalizeCodeItemReference(group.codeItemPos.code)
  if (!optionCode || !ownReference) return null

  const digits = getCodeItemDigits(group)
  const ownedPositions = new Set(getValueOwnedPositions(row, digits))
  let error: RuleValidationError | null = null

  visitRuleConstraintEntries(rule, (condition) => {
    if (error || !sameReference(condition.reference, group.codeItemPos.code)) return

    const clashingPosition = getConditionTouchedPositions(condition, digits)
      .find(position => ownedPositions.has(position))
    if (clashingPosition === undefined) return

    const atom = conditionEntryToAtom(condition)
    if (!atom) return

    // A positioned value occupies a slot of a composite Code Item, so the message names it;
    // a value that owns the whole Code Item is named in full.
    const namesSlot = ownedPositions.size < digits
    const slotLabel = namesSlot
      ? `${formatCodeItemReference(group.codeItemPos.code)} position ${clashingPosition + 1}`
      : formatCodeItemReference(group.codeItemPos.code)
    const ownText = namesSlot ? optionCode[clashingPosition] || optionCode : optionCode
    const requiredText = namesSlot
      ? getConditionCharacterAt(condition, clashingPosition)
      : atom.value || getConditionCharacterAt(condition, clashingPosition)
    const isContradiction = atom.condition === '==' && Boolean(requiredText) && requiredText !== ownText

    error = {
      type: 'own-position',
      message: isContradiction
        ? `This condition can never be met. ${slotLabel} is already ${ownText}, so it cannot also be ${requiredText}.`
        : `This rule cannot constrain ${slotLabel}, because value ${optionCode} already sets it to ${ownText}.`,
      localConditions: [conditionAtomToHighlight(atom)],
    }
  })

  return error
}

// -----------------------------
// Rule B: impossible value, Rule D: the values it requires, Rule E: a dead alternative
// -----------------------------

/**
 * The search walks (Code Item, option) pairs, which form a cyclic graph by design: two values may
 * legally require each other. These bounds keep a pathological sheet from stalling the save path;
 * exceeding either one answers "viable", so validation never blocks on a search it could not finish.
 */
const MAX_VIABILITY_DEPTH = 6
const MAX_VIABILITY_VISITS = 400
const MAX_MESSAGE_STEPS = 3

/** One hop of an explanation: the rule of `optionCode` leads on through `atom`. */
type ViabilityStep = {
  codeItemCode: string
  optionCode: string
  atom: ConditionAtom
}

type ViabilityRejection = {
  steps: ViabilityStep[]
  unmatched: { reference: string; atoms: ConditionAtom[] } | null
}

type ViabilitySearch = {
  groups: CodeItemGroup[]
  groupsByReference: Map<string, CodeItemGroup>
  targetGroup: CodeItemGroup
  targetOptionCode: string
  resolved: Map<string, ViabilityRejection | null>
  inProgress: Set<string>
  visits: number
}

function createViabilitySearch(
  groups: CodeItemGroup[],
  targetGroup: CodeItemGroup,
  targetOptionCode: string
): ViabilitySearch {
  return {
    groups,
    groupsByReference: indexGroupsByReference(groups),
    targetGroup,
    targetOptionCode,
    resolved: new Map<string, ViabilityRejection | null>(),
    inProgress: new Set<string>(),
    visits: 0,
  }
}

const groupsByReferenceCache = new WeakMap<object, Map<string, CodeItemGroup>>()

function indexGroupsByReference(groups: CodeItemGroup[]): Map<string, CodeItemGroup> {
  const cached = groupsByReferenceCache.get(groups)
  if (cached) return cached

  const index = new Map<string, CodeItemGroup>()
  groups.forEach((group) => {
    const key = normalizeCodeItemReference(group.codeItemPos.code)
    if (key && !index.has(key)) index.set(key, group)
  })
  groupsByReferenceCache.set(groups, index)
  return index
}

function groupAtomsByReference(path: ConditionAtom[]): Map<string, ConditionAtom[]> {
  const atomsByReference = new Map<string, ConditionAtom[]>()

  path.forEach((atom) => {
    const key = normalizeCodeItemReference(atom.reference)
    if (!key) return
    atomsByReference.set(key, [...(atomsByReference.get(key) || []), atom])
  })

  return atomsByReference
}

function isFollowableRule(rule: RuleNode | null | undefined): boolean {
  if (!rule || rule.type === 'text') return false
  // Only a plain availability rule is a requirement of its value; an IF/THEN rule is conditional.
  if (rule.type === 'if' && (rule.consequenceGroups || []).filter(Boolean).length > 0) return false
  return true
}

/**
 * Rule D: why option `optionCode` of `group` cannot hold while the value being validated is
 * selected, or null when it can. The search follows requirements as far as they go; a pair that is
 * already being decided counts as viable, because values that require each other are satisfied by
 * selecting them together.
 */
function findOptionRejection(
  search: ViabilitySearch,
  group: CodeItemGroup,
  optionCode: string,
  depth: number
): ViabilityRejection | null {
  const key = `${normalizeCodeItemReference(group.codeItemPos.code)}|||${optionCode}`
  if (search.inProgress.has(key)) return null

  const resolved = search.resolved.get(key)
  if (resolved !== undefined) return resolved
  if (depth > MAX_VIABILITY_DEPTH || search.visits >= MAX_VIABILITY_VISITS) return null

  search.visits += 1

  const row = group.rows.find(candidate => normalizeOptionPattern(candidate.optionCode) === optionCode)
  // A value with no rule of its own is always viable.
  if (!isFollowableRule(row?.rule)) {
    search.resolved.set(key, null)
    return null
  }

  const paths = getRuleConditionPaths(row?.rule)
  if (paths.length === 0) {
    search.resolved.set(key, null)
    return null
  }

  search.inProgress.add(key)

  let rejection: ViabilityRejection | null = null

  for (const path of paths) {
    const pathRejection = findPathRejection(search, path, group, optionCode, depth)
    if (!pathRejection) {
      rejection = null
      break
    }
    if (!rejection) rejection = pathRejection
  }

  search.inProgress.delete(key)
  search.resolved.set(key, rejection)
  return rejection
}

/** Why one AND path of `optionCode`'s own rule cannot hold, or null when it can. */
function findPathRejection(
  search: ViabilitySearch,
  path: ConditionAtom[],
  group: CodeItemGroup,
  optionCode: string,
  depth: number
): ViabilityRejection | null {
  const targetKey = normalizeCodeItemReference(search.targetGroup.codeItemPos.code)
  const atomsByReference = groupAtomsByReference(path)

  // What this path demands of the value being validated has to hold for that value's own pattern.
  const failingAtom = (atomsByReference.get(targetKey) || [])
    .find(atom => !optionSatisfiesAtom(search.targetOptionCode, atom))
  if (failingAtom) {
    return {
      steps: [{ codeItemCode: group.codeItemPos.code, optionCode, atom: failingAtom }],
      unmatched: null,
    }
  }

  let rejection: ViabilityRejection | null = null

  atomsByReference.forEach((atoms, key) => {
    if (rejection || key === targetKey) return

    const referencedGroup = search.groupsByReference.get(key)
    // A Code Item that is not on the sheet is skipped.
    if (!referencedGroup || referencedGroup.rows.length === 0) return

    const step = { codeItemCode: group.codeItemPos.code, optionCode, atom: atoms[0] }
    const matchingOptions = getMatchingOptions(referencedGroup, atoms)
    if (matchingOptions.length === 0) {
      rejection = { steps: [step], unmatched: { reference: atoms[0].reference, atoms } }
      return
    }

    let firstRejection: ViabilityRejection | null = null
    let hasViableOption = false

    for (const candidate of matchingOptions) {
      const candidateRejection = findOptionRejection(search, referencedGroup, candidate, depth + 1)
      // One option that can hold keeps this requirement alive.
      if (!candidateRejection) {
        hasViableOption = true
        break
      }
      if (!firstRejection) firstRejection = candidateRejection
    }

    if (!hasViableOption && firstRejection) {
      rejection = { steps: [step, ...firstRejection.steps], unmatched: firstRejection.unmatched }
    }
  })

  return rejection
}

type TargetPathFailure = {
  /** Conditions of the rule being validated, for highlighting. */
  atoms: ConditionAtom[]
  rejection: ViabilityRejection
}

/** Why one AND path of the rule being validated cannot hold, or null when it can. */
function findTargetPathFailure(
  search: ViabilitySearch,
  path: ConditionAtom[],
  followRequiredValues: boolean
): TargetPathFailure | null {
  const atomsByReference = groupAtomsByReference(path)
  let failure: TargetPathFailure | null = null

  atomsByReference.forEach((atoms, key) => {
    if (failure) return

    const group = search.groupsByReference.get(key)
    // A Code Item that is not on the sheet is skipped; validateRuleValueReferences reports unknown values.
    if (!group || group.rows.length === 0) return

    const matchingOptions = getMatchingOptions(group, atoms)
    if (matchingOptions.length === 0) {
      failure = { atoms, rejection: { steps: [], unmatched: { reference: atoms[0].reference, atoms } } }
      return
    }

    if (!followRequiredValues) return

    let firstRejection: ViabilityRejection | null = null
    let hasViableOption = false

    for (const optionCode of matchingOptions) {
      const rejection = findOptionRejection(search, group, optionCode, 1)
      if (!rejection) {
        hasViableOption = true
        break
      }
      if (!firstRejection) firstRejection = rejection
    }

    if (!hasViableOption && firstRejection) failure = { atoms, rejection: firstRejection }
  })

  return failure
}

function formatViabilityStep(step: ViabilityStep, leadWithValue: boolean): string {
  const owner = leadWithValue
    ? `value ${step.optionCode} of ${formatCodeItemReference(step.codeItemCode)}`
    : `${formatCodeItemReference(step.codeItemCode)} value ${step.optionCode}`

  return `${owner} requires ${formatConditionAtom(step.atom)}`
}

function formatUnmatchedFragment(
  unmatched: { reference: string; atoms: ConditionAtom[] },
  lastStep: ViabilityStep | undefined
): string {
  if (lastStep && unmatched.atoms.length === 1 && unmatched.atoms[0] === lastStep.atom) {
    return `no option on ${formatCodeItemReference(unmatched.reference)} matches it`
  }

  return `no option on ${formatCodeItemReference(unmatched.reference)} satisfies ${unmatched.atoms.map(formatConditionAtom).join(' and ')}`
}

/**
 * The explanation of a rejection, capped so a long chain stays readable: the first hops, an ellipsis
 * and the rule that finally rejects the value.
 */
function formatViabilityRejection(rejection: ViabilityRejection, leadWithValue: boolean): string {
  const { steps } = rejection
  const fragments = steps.length > MAX_MESSAGE_STEPS
    ? [
        ...steps.slice(0, MAX_MESSAGE_STEPS - 1).map((step, index) => formatViabilityStep(step, leadWithValue && index === 0)),
        '...',
        formatViabilityStep(steps[steps.length - 1], false),
      ]
    : steps.map((step, index) => formatViabilityStep(step, leadWithValue && index === 0))

  if (rejection.unmatched) {
    fragments.push(formatUnmatchedFragment(rejection.unmatched, steps[steps.length - 1]))
  }

  if (fragments.length === 0) return ''

  const sentence = `${fragments.join(', and ')}.`
  if (leadWithValue || steps.length > 0) return sentence

  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}`
}

/**
 * A rule whose every AND path is impossible can never be satisfied, so the value it belongs to could
 * never be selected. The options a path requires are followed as far as their own rules go: an
 * option whose requirements cannot hold while this value is selected does not keep the path alive.
 */
export function findUnsatisfiableRule(
  rule: RuleNode | null | undefined,
  context: RuleValidationContext | null | undefined
): RuleValidationError | null {
  if (!rule || rule.type === 'text' || !context) return null

  const group = context.groups[context.targetGroupIndex]
  const row = group?.rows[context.targetRowIndex]
  if (!group || !row) return null

  const optionCode = normalizeOptionPattern(row.optionCode)
  if (!optionCode) return null

  const paths = getRuleConditionPaths(rule)
  if (paths.length === 0) return null

  const search = createViabilitySearch(context.groups, group, optionCode)
  const followRequiredValues = isFollowableRule(rule) && context.followRequiredValues !== false
  const failures = paths.map(path => findTargetPathFailure(search, path, followRequiredValues))
  const firstFailure = failures.find((failure): failure is TargetPathFailure => Boolean(failure))
  if (!firstFailure || failures.some(failure => !failure)) return null

  return {
    type: 'impossible',
    message: `Cannot save rule. This rule can never be satisfied, so value ${optionCode} of ${formatCodeItemReference(group.codeItemPos.code)} could never be selected. ${formatViabilityRejection(firstFailure.rejection, false)}`,
    localConditions: firstFailure.atoms.map(conditionAtomToHighlight),
  }
}

/**
 * Rule E: a value the rule names that can never hold, while the rule as a whole still can. The rule
 * saves; the condition is flagged so the author can see that branch is dead.
 */
export function findDeadAlternativeWarnings(
  rule: RuleNode | null | undefined,
  context: RuleValidationContext | null | undefined
): RuleValidationWarning[] {
  if (!rule || rule.type === 'text' || !context || !isFollowableRule(rule)) return []
  if (context.followRequiredValues === false) return []

  const group = context.groups[context.targetGroupIndex]
  const row = group?.rows[context.targetRowIndex]
  if (!group || !row) return []

  const optionCode = normalizeOptionPattern(row.optionCode)
  if (!optionCode) return []

  const search = createViabilitySearch(context.groups, group, optionCode)
  const warnings: RuleValidationWarning[] = []
  const seenAtoms = new Set<string>()

  getRuleConditionPaths(rule).forEach((path) => {
    path.forEach((atom) => {
      // Only an equality names the value it needs; an exclusion or a comparison does not.
      if (atom.condition !== '==') return

      const atomText = formatConditionAtom(atom)
      if (seenAtoms.has(atomText)) return

      const referencedGroup = search.groupsByReference.get(normalizeCodeItemReference(atom.reference))
      if (!referencedGroup || referencedGroup.rows.length === 0) return

      const matchingOptions = getMatchingOptions(referencedGroup, [atom])
      let rejection: ViabilityRejection | null = null
      let hasViableOption = false

      if (matchingOptions.length === 0) {
        rejection = { steps: [], unmatched: { reference: atom.reference, atoms: [atom] } }
      }

      for (const candidate of matchingOptions) {
        const candidateRejection = findOptionRejection(search, referencedGroup, candidate, 1)
        if (!candidateRejection) {
          hasViableOption = true
          break
        }
        if (!rejection) rejection = candidateRejection
      }

      if (hasViableOption || !rejection) return

      seenAtoms.add(atomText)
      warnings.push({
        type: 'dead-alternative',
        message: `${atomText} can never hold, because ${formatViabilityRejection(rejection, true)}`,
        localConditions: [conditionAtomToHighlight(atom)],
      })
    })
  })

  return warnings
}

// -----------------------------
// Rule C: dead IF
// -----------------------------

/** An IF side that cannot match the value it is attached to: the rule can never apply. */
export function findDeadTriggerWarning(
  rule: RuleNode | null | undefined,
  context: RuleValidationContext | null | undefined
): RuleValidationWarning | null {
  if (!rule || rule.type !== 'if' || !context) return null
  if ((rule.consequenceGroups || []).filter(Boolean).length === 0) return null

  const group = context.groups[context.targetGroupIndex]
  const row = group?.rows[context.targetRowIndex]
  if (!group || !row) return null

  const optionCode = normalizeOptionPattern(row.optionCode)
  if (!optionCode) return null

  const paths = getRuleTriggerPaths(rule)
  if (paths.length === 0) return null

  let deadAtoms: ConditionAtom[] | null = null

  for (const path of paths) {
    const ownAtoms = path.filter(atom => sameReference(atom.reference, group.codeItemPos.code))
    if (ownAtoms.length === 0) return null
    if (ownAtoms.every(atom => optionSatisfiesAtom(optionCode, atom))) return null
    if (!deadAtoms) deadAtoms = ownAtoms
  }

  if (!deadAtoms) return null

  return {
    type: 'dead-trigger',
    message: `This rule can never apply to value ${optionCode}, because its IF side requires ${deadAtoms.map(formatConditionAtom).join(' and ')}.`,
    localConditions: deadAtoms.map(conditionAtomToHighlight),
  }
}

// -----------------------------
// Numeric checks
// -----------------------------

function findInvalidNumericRange(rule: RuleNode | null | undefined): RuleValidationError | null {
  let issue: RuleValidationError | null = null

  visitRuleEntries(rule, condition => {
    if (issue || normalizeRuleValue(condition.condition) !== 'between') return

    const atom = conditionEntryToAtom(condition)
    if (!atom || atom.values) return

    const fromValue = parseNumericValue(atom.from)
    const toValue = parseNumericValue(atom.to)

    if (fromValue !== null && toValue !== null && fromValue > toValue) {
      issue = {
        type: 'range',
        message: `Cannot save rule. Between range for ${formatCodeItemReference(atom.reference)} has low value ${atom.from} greater than high value ${atom.to}.`,
        localConditions: [conditionAtomToHighlight(atom)],
      }
    }
  })

  return issue
}

function findNumericWarnings(rule: RuleNode | null | undefined): RuleValidationWarning[] {
  const warnings: RuleValidationWarning[] = []
  const seenConditions = new Set<string>()

  visitRuleEntries(rule, condition => {
    const atom = conditionEntryToAtom(condition)
    if (!atom || atom.values || !NUMERIC_OPERATORS.has(atom.condition)) return

    const values = atom.condition === 'between' ? [atom.from, atom.to] : [atom.value]
    const hasNonnumericValue = values.some(value => parseNumericValue(value) === null)
    if (!hasNonnumericValue) return

    const conditionText = formatConditionAtom(atom)
    if (seenConditions.has(conditionText)) return

    seenConditions.add(conditionText)
    warnings.push({
      type: 'numeric-operator',
      message: `${conditionText} uses a numeric operator with nonnumeric values. Comparison will use alphabetic ordering until value attributes are structured.`,
      localConditions: [conditionAtomToHighlight(atom)],
    })
  })

  return warnings
}

export function validateRuleLogic(
  rule: RuleNode | null | undefined,
  context?: RuleValidationContext
): { errors: RuleValidationError[]; warnings: RuleValidationWarning[] } {
  const errors = [
    findOwnSubPositionError(rule, context),
    findUnsatisfiableRule(rule, context),
    findContradiction(rule),
    findInvalidNumericRange(rule),
  ].filter((issue): issue is RuleValidationError => Boolean(issue))

  // A rule that can never be satisfied is already an error; its dead branches would only repeat it.
  const deadAlternativeWarnings = errors.some(error => error.type === 'impossible')
    ? []
    : findDeadAlternativeWarnings(rule, context)

  const warnings = [
    ...findNumericWarnings(rule),
    findDeadTriggerWarning(rule, context),
    ...deadAlternativeWarnings,
  ].filter((issue): issue is RuleValidationWarning => Boolean(issue))

  return { errors, warnings }
}
